import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Rocket, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SEVERITY_STYLE = {
  critical: "bg-destructive/10 text-destructive border-destructive/30",
  high: "bg-gold/15 text-gold border-gold/30",
  medium: "bg-deepgreen/10 text-deepgreen border-deepgreen/30",
  low: "bg-muted text-muted-foreground",
};

export default function GoLive() {
  const [data, setData] = useState(null);
  async function refresh() { try { const { data } = await api.get("/go-live/status"); setData(data); } catch {} }
  useEffect(() => { refresh(); }, []);
  async function toggle(key, passed) { await api.post("/go-live/confirm", { key, passed }); refresh(); }
  async function override(key, justification) {
    try { await api.post("/go-live/override", { key, justification }); toast.success("Override recorded"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function publish(acceptRisks, notes) {
    try { await api.post("/go-live/publish", { accept_risks: acceptRisks, notes }); toast.success("Home360 published to production"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  if (!data) return <div className="p-8 subtle">Loading go-live readiness…</div>;

  return (
    <div className="space-y-6" data-testid="go-live-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Release</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Go-Live Wizard</h1>
        <p className="subtle mt-1">Risk-based publication readiness. Critical checks block publish; high checks require justification.</p>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Readiness score</div>
            <div className="font-heading text-4xl font-semibold" data-testid="readiness-score">{data.readiness_score}%</div>
            <Progress value={data.readiness_score} className="mt-2" />
          </div>
          <Stat label="Critical failed" value={data.critical_failed} tone={data.critical_failed ? "destructive" : "ok"} />
          <Stat label="High failed" value={data.high_failed} tone={data.high_failed ? "gold" : "ok"} />
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Status</div>
            <div className="mt-1 font-medium">
              {data.published_at ? <><Badge className="bg-deepgreen text-deepgreen-foreground">Published</Badge></> : <Badge variant="outline">Pre-production</Badge>}
            </div>
            {data.published_at && <div className="text-xs subtle mt-1">at {data.published_at}</div>}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-deepgreen" />Checklist</CardTitle>
          <CardDescription>Toggle a check when validated. Overrides are recorded to the audit log.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-border">
            {data.items.map((it) => (
              <li key={it.key} className="py-3 flex items-start gap-3" data-testid={`check-${it.key}`}>
                <div className="pt-0.5">
                  {it.passed || it.override ? <CheckCircle2 className="h-5 w-5 text-deepgreen" /> : <XCircle className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{it.label}</div>
                    <Badge variant="outline" className={SEVERITY_STYLE[it.severity]}>{it.severity}</Badge>
                  </div>
                  <div className="text-xs subtle">{it.auto ? "auto-detected pass" : "auto-detected fail"}{it.manual !== null && it.manual !== undefined ? ` · manual: ${it.manual}` : ""}{it.override ? ` · overridden by ${it.override.by}` : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs flex items-center gap-1">
                    <Checkbox checked={!!it.passed} onCheckedChange={(v) => toggle(it.key, !!v)} data-testid={`toggle-${it.key}`} />
                    <span>Passed</span>
                  </label>
                  {!it.passed && !it.override && (
                    <OverrideDialog check={it} onOverride={override} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <PublishDialog canPublish={data.can_publish} highFailed={data.high_failed} onPublish={publish} />
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "destructive" ? "text-destructive" : tone === "gold" ? "text-gold" : "text-deepgreen";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-heading text-4xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function OverrideDialog({ check, onOverride }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost" className="text-xs">Override</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Override — {check.label}</DialogTitle></DialogHeader>
        <div><label className="text-sm">Justification (audit-logged)</label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" /></div>
        <DialogFooter><Button onClick={() => { if (reason.trim()) { onOverride(check.key, reason); setOpen(false); } }}>Record override</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PublishDialog({ canPublish, highFailed, onPublish }) {
  const [open, setOpen] = useState(false);
  const [accept, setAccept] = useState(false);
  const [notes, setNotes] = useState("");
  return (
    <Card className="rounded-2xl border-deepgreen/30 bg-deepgreen/5">
      <CardContent className="pt-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <div className="font-heading text-lg font-semibold">Publish Home360 to production</div>
          <div className="subtle text-sm">Available once all Critical checks are green. High-severity risks require explicit acceptance.</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!canPublish} className="bg-deepgreen hover:bg-deepgreen/90" data-testid="publish-btn">
              <Rocket className="h-4 w-4 mr-1" /> Publish
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Confirm production publish</DialogTitle></DialogHeader>
            <p className="text-sm subtle">The publish decision will be recorded in the audit log.</p>
            {highFailed > 0 && (
              <label className="flex items-start gap-2 text-sm mt-3">
                <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="accept-risks" />
                <span>I accept the {highFailed} high-severity risk(s) that remain outstanding.</span>
              </label>
            )}
            <div className="mt-3"><label className="text-xs">Notes</label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <DialogFooter><Button onClick={() => { onPublish(accept, notes); setOpen(false); }} className="bg-deepgreen hover:bg-deepgreen/90">Publish now</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
